import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import PageHeader from '../components/layout/PageHeader';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { staffChipClass } from '../components/layout/staffPageStyles';
import { BadgeCheck, Bot, Check, Database, RefreshCw, Search, Settings, X } from 'lucide-react';

// Backend-keyed maps -> i18n keys (display only, raw value kept as fallback).
const srcLabel = { vente_pos: 'adminCadbotPrix.source.ventePos', reparation: 'adminCadbotPrix.source.reparation', devis_valide: 'adminCadbotPrix.source.devisValide' };
const refLabel = { prix_valide: 'adminCadbotPrix.refSource.prixValide', tarif_public: 'adminCadbotPrix.refSource.tarifPublic', calcul_fournisseur: 'adminCadbotPrix.refSource.calculFournisseur', aucune: 'adminCadbotPrix.refSource.aucune' };

export default function AdminCadbotPrixPage() {
  const { api } = useAuth();
  const { t, formatCurrency, formatNumber } = useLanguage();
  const fmt = (n) => (n === null || n === undefined ? '—' : formatCurrency(n));
  const [filter, setFilter] = useState('pending');
  const [rows, setRows] = useState([]);
  const [validated, setValidated] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [edits, setEdits] = useState({});
  const [showSettings, setShowSettings] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, v, s] = await Promise.all([
        api.get(`/admin/cadbot-prix/proposals?status=${filter}`),
        api.get('/admin/cadbot-prix/validated'),
        api.get('/admin/cadbot-prix/settings'),
      ]);
      setRows(p.data.proposals || []);
      setValidated(v.data.validated || []);
      setSettings(s.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('adminCadbotPrix.loadError'));
    } finally {
      setLoading(false);
    }
  }, [api, filter, t]);

  useEffect(() => { load(); }, [load]);

  const run = async (label, fn) => {
    setBusy(label);
    try {
      const res = await fn();
      return res;
    } catch (e) {
      toast.error(e.response?.data?.detail || t('adminCadbotPrix.genericError'));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const collect = async () => {
    const res = await run('collect', () => api.post('/admin/cadbot-prix/collect'));
    if (res) toast.success(t('adminCadbotPrix.toastCollected', { ventes: res.data.lignes_memorisees, reparations: res.data.reparations }));
    await load();
  };

  const analyze = async () => {
    const res = await run('analyze', () => api.post('/admin/cadbot-prix/analyze'));
    if (res) toast.success(t('adminCadbotPrix.toastAnalyzed', { creees: res.data.propositions_creees, maj: res.data.propositions_mises_a_jour }));
    setFilter('pending');
    await load();
  };

  const approve = async (p) => {
    const price = edits[p.id] !== undefined && edits[p.id] !== '' ? parseFloat(edits[p.id]) : null;
    if (price !== null && (Number.isNaN(price) || price <= 0)) { toast.error(t('adminCadbotPrix.priceInvalid')); return; }
    const res = await run(p.id, () => api.post(`/admin/cadbot-prix/proposals/${p.id}/approve`, { price }));
    if (res) toast.success(t('adminCadbotPrix.toastApproved', { price: fmt(res.data.validated.prix_final), detail: res.data.service_price.action === 'locked_pending' ? t('adminCadbotPrix.toastApprovedLocked') : t('adminCadbotPrix.toastApprovedPublished') }));
    await load();
  };

  const reject = async (p) => {
    const reason = window.prompt(t('adminCadbotPrix.rejectReasonPrompt'), '') ?? null;
    if (reason === null) return;
    const res = await run(p.id, () => api.post(`/admin/cadbot-prix/proposals/${p.id}/reject`, { reason }));
    if (res) toast.success(t('adminCadbotPrix.proposalRejected'));
    await load();
  };

  const saveSettings = async (patch) => {
    const res = await run('settings', () => api.put('/admin/cadbot-prix/settings', patch));
    if (res) { setSettings(res.data); toast.success(t('adminCadbotPrix.settingsSaved')); }
  };

  const removeValidated = async (v) => {
    if (!window.confirm(t('adminCadbotPrix.confirmRemoveValidated', { model: v.model, part: v.part, quality: v.quality }))) return;
    const res = await run(v.key, () => api.delete(`/admin/cadbot-prix/validated/${encodeURIComponent(v.key)}`));
    if (res) toast.success(t('adminCadbotPrix.validatedRemoved'));
    await load();
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6 fade-in" data-testid="admin-cadbot-prix">
        <PageHeader
          title={t('adminCadbotPrix.pageTitle')}
          subtitle={t('adminCadbotPrix.pageSubtitle')}
          icon={Bot}
          actions={(
            <>
              <Button variant="outline" size="sm" onClick={() => setShowSettings((s) => !s)} className="border-gray-600 text-gray-300" data-testid="cadbot-settings-btn">
                <Settings className="w-4 h-4 mr-2" /> {t('adminCadbotPrix.rules')}
              </Button>
              <Button variant="outline" size="sm" onClick={collect} disabled={busy === 'collect'} className="border-gray-600 text-gray-300" data-testid="cadbot-collect-btn">
                <Database className="w-4 h-4 mr-2" /> {busy === 'collect' ? t('adminCadbotPrix.collecting') : t('adminCadbotPrix.memorizeSales')}
              </Button>
              <Button size="sm" onClick={analyze} disabled={busy === 'analyze'} className="bg-red-600 hover:bg-red-700 text-white" data-testid="cadbot-analyze-btn">
                <Search className="w-4 h-4 mr-2" /> {busy === 'analyze' ? t('adminCadbotPrix.analyzing') : t('adminCadbotPrix.analyze')}
              </Button>
            </>
          )}
        />

        {showSettings && settings && (
          <div className="glass p-4 rounded-2xl border border-white/10 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="cadbot-settings">
            {[
              ['min_sales', t('adminCadbotPrix.minSales'), 1, 1],
              ['tolerance_pct', t('adminCadbotPrix.tolerancePct'), 0.5, 0],
              ['tolerance_eur', t('adminCadbotPrix.toleranceEur'), 0.5, 0],
              ['window_days', t('adminCadbotPrix.windowDays'), 30, 30],
            ].map(([k, label, step, min]) => (
              <label key={k} className="space-y-1">
                <span className="text-xs text-gray-500">{label}</span>
                <input
                  type="number" step={step} min={min} defaultValue={settings[k]}
                  onBlur={(e) => { const v = parseFloat(e.target.value); if (!Number.isNaN(v) && v !== settings[k]) saveSettings({ [k]: v }); }}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-white"
                />
              </label>
            ))}
            <label className="flex items-center gap-2 col-span-2 text-gray-300">
              <input type="checkbox" checked={!!settings.publish_on_approve} onChange={(e) => saveSettings({ publish_on_approve: e.target.checked })} />
              {t('adminCadbotPrix.publishOnApprove')}
            </label>
            <label className="flex items-center gap-2 col-span-2 text-gray-300">
              <input type="checkbox" checked={!!settings.count_devis_valides} onChange={(e) => saveSettings({ count_devis_valides: e.target.checked })} />
              {t('adminCadbotPrix.countDevisValides')}
            </label>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {['pending', 'approved', 'rejected', 'all'].map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={staffChipClass(filter === s)} data-testid={`cadbot-filter-${s}`}>
              {{ pending: t('adminCadbotPrix.filterPending'), approved: t('adminCadbotPrix.filterApproved'), rejected: t('adminCadbotPrix.filterRejected'), all: t('adminCadbotPrix.filterAll') }[s]}
            </button>
          ))}
          <Button variant="outline" size="sm" onClick={load} className="border-gray-600 text-gray-300 ml-auto"><RefreshCw className="w-4 h-4 mr-2" /> {t('adminCadbotPrix.refresh')}</Button>
        </div>

        {loading ? (
          <div className="text-center text-gray-400 py-12">{t('adminCadbotPrix.loading')}</div>
        ) : rows.length === 0 ? (
          <div className="glass p-10 rounded-2xl border border-white/10 text-center text-gray-400">
            <Bot className="w-10 h-10 mx-auto mb-3 text-gray-600" />
            {t('adminCadbotPrix.emptyProposals', { scope: filter === 'pending' ? t('adminCadbotPrix.emptyScopePending') : '', collect: t('adminCadbotPrix.memorizeSales'), analyze: t('adminCadbotPrix.analyze') })}
          </div>
        ) : (
          <div className="space-y-4">
            {rows.map((p) => (
              <div key={p.id} className="glass p-4 sm:p-5 space-y-4 border border-white/10 rounded-2xl" data-testid={`cadbot-proposal-${p.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-white font-bold text-base">{p.marque} · {p.model} · {p.type_reparation}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {t('adminCadbotPrix.tier')} <span className="text-gray-300">{p.quality}</span> · {t('adminCadbotPrix.realSales', { count: p.n_sales, min: fmt(p.price_min), max: fmt(p.price_max) })} · {t('adminCadbotPrix.lastSaleOn', { date: p.last_sale_date })}
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${p.status === 'pending' ? 'bg-amber-500/15 text-amber-400' : p.status === 'approved' ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>
                    {p.status === 'pending' ? t('adminCadbotPrix.statusBadgePending') : p.status === 'approved' ? t('adminCadbotPrix.statusBadgeApproved') : t('adminCadbotPrix.statusBadgeRejected')}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-xs text-gray-500">{t('adminCadbotPrix.referencePrice')} ({refLabel[p.reference_source] ? t(refLabel[p.reference_source]) : p.reference_source})</div>
                    <div className="text-white font-semibold text-lg">{fmt(p.reference_price)}</div>
                  </div>
                  <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-xs text-gray-500">{t('adminCadbotPrix.medianRealSales')}</div>
                    <div className="text-green-400 font-bold text-lg">{fmt(p.proposed_price)}</div>
                    {p.deviation_pct !== null && p.deviation_pct !== undefined && (
                      <div className={`text-xs ${p.deviation_eur < 0 ? 'text-red-400' : 'text-amber-400'}`}>{p.deviation_eur > 0 ? '+' : ''}{formatCurrency(p.deviation_eur)} ({p.deviation_pct > 0 ? '+' : ''}{formatNumber(p.deviation_pct)} %)</div>
                    )}
                  </div>
                  <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-xs text-gray-500">{p.status === 'pending' ? t('adminCadbotPrix.priceToValidate') : t('adminCadbotPrix.priceRetained')}</div>
                    {p.status === 'pending' ? (
                      <input
                        type="number" step="0.01" min="1" placeholder={String(p.proposed_price)}
                        value={edits[p.id] ?? ''} onChange={(e) => setEdits((x) => ({ ...x, [p.id]: e.target.value }))}
                        className="w-full mt-1 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white"
                        data-testid={`cadbot-price-input-${p.id}`}
                      />
                    ) : (
                      <div className="text-white font-semibold text-lg">{fmt(p.final_price)}</div>
                    )}
                  </div>
                </div>

                <details className="text-xs text-gray-400">
                  <summary className="cursor-pointer text-gray-300">{t('adminCadbotPrix.evidenceSales', { count: (p.evidence || []).length })}</summary>
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="text-gray-500 border-b border-white/10"><tr><th className="text-left py-1">{t('adminCadbotPrix.thDate')}</th><th className="text-left">{t('adminCadbotPrix.thSource')}</th><th className="text-left">{t('adminCadbotPrix.thRef')}</th><th className="text-left">{t('adminCadbotPrix.thDesignation')}</th><th className="text-right">{t('adminCadbotPrix.thPriceTtc')}</th></tr></thead>
                      <tbody>
                        {(p.evidence || []).map((e, i) => (
                          <tr key={i} className="border-b border-white/5"><td className="py-1">{e.date}</td><td>{srcLabel[e.source] ? t(srcLabel[e.source]) : e.source}</td><td className="font-mono">{e.source_ref}</td><td className="text-gray-300">{e.designation}</td><td className="text-right text-white">{fmt(e.prix_ttc)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>

                {p.status === 'pending' ? (
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
                    <Button size="sm" onClick={() => approve(p)} disabled={busy === p.id} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`cadbot-approve-${p.id}`}>
                      <Check className="w-4 h-4 mr-1" /> {t('adminCadbotPrix.validate')}{edits[p.id] ? ` ${t('adminCadbotPrix.validateAt', { price: edits[p.id] })}` : ''}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => reject(p)} disabled={busy === p.id} className="border-red-600 text-red-400 hover:bg-red-600/10" data-testid={`cadbot-reject-${p.id}`}>
                      <X className="w-4 h-4 mr-1" /> {t('adminCadbotPrix.reject')}
                    </Button>
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 pt-2 border-t border-white/10">
                    {p.status === 'approved' ? t('adminCadbotPrix.reviewApproved', { by: p.reviewed_by, date: p.reviewed_at?.slice(0, 10) }) : t('adminCadbotPrix.reviewRejected', { by: p.reviewed_by, date: p.reviewed_at?.slice(0, 10) })}{p.note ? ` — ${p.note}` : ''}
                    {p.service_price?.action === 'locked_pending' && <span className="ml-2 text-amber-400">{t('adminCadbotPrix.lockedPendingNote')}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="glass p-4 sm:p-5 border border-white/10 rounded-2xl space-y-3" data-testid="cadbot-validated">
          <h2 className="text-white font-bold flex items-center gap-2"><BadgeCheck className="w-5 h-5 text-green-400" /> {t('adminCadbotPrix.validatedPrices', { count: validated.length })}</h2>
          {validated.length === 0 ? (
            <p className="text-sm text-gray-500">{t('adminCadbotPrix.noValidatedPrices')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-gray-400 border-b border-white/10"><tr><th className="text-left py-2">{t('adminCadbotPrix.thDevice')}</th><th className="text-left">{t('adminCadbotPrix.thPart')}</th><th className="text-left">{t('adminCadbotPrix.thTier')}</th><th className="text-right">{t('adminCadbotPrix.thPrice')}</th><th className="text-left pl-3">{t('adminCadbotPrix.thValidated')}</th><th></th></tr></thead>
                <tbody>
                  {validated.map((v) => (
                    <tr key={v.key} className="border-b border-white/5">
                      <td className="py-2 text-white">{v.brand} {v.model}</td><td className="text-gray-300">{v.part}</td><td className="text-gray-300">{v.quality}</td>
                      <td className="text-right text-green-400 font-semibold">{fmt(v.prix_final)}</td>
                      <td className="pl-3 text-xs text-gray-500">{v.reviewed_by} · {v.reviewed_at?.slice(0, 10)} · {t('adminCadbotPrix.salesCountShort', { count: v.n_sales })}</td>
                      <td className="text-right"><button onClick={() => removeValidated(v)} className="text-xs text-red-400 hover:underline">{t('adminCadbotPrix.remove')}</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
